<template>
  <div class="dc">
    <div class="dc-toolbar">
      <input
        v-model.trim="query"
        class="dc-search"
        type="search"
        placeholder="Search tables and descriptions…"
      >
      <span v-if="query" class="dc-count">{{ matchCount }} matches</span>
      <span v-if="meta" class="dc-count dc-meta">{{ meta }}</span>
    </div>

    <div class="dc-body">
      <aside class="dc-list">
        <div v-for="col in columns" :key="col.layer" class="dc-layer">
          <div class="dc-layer-head">
            <span class="dc-chip" :class="layerClass(col.layer)">{{ pretty(col.layer) }}</span>
            <span class="dc-count">{{ col.total }}</span>
            <span class="dc-layer-hint">{{ col.hint }}</span>
          </div>

          <div v-for="grp in col.groups" :key="col.layer + grp.schema" class="dc-group">
            <button
              type="button"
              class="dc-schema"
              @click="toggleSchema(col.layer, grp.schema)"
            >
              <span class="dc-caret">{{ isOpen(col.layer, grp.schema) ? '−' : '+' }}</span>
              <span class="dc-trunc">{{ grp.schema }}</span>
              <span class="dc-count">{{ grp.tables.length }}</span>
            </button>
            <template v-if="isOpen(col.layer, grp.schema)">
              <button
                v-for="t in grp.tables"
                :key="t.id"
                type="button"
                class="dc-row"
                :class="{ on: t.id === selected }"
                :title="t.desc || t.id"
                @click="selectTable(t.id)"
              >
                <span class="dc-trunc">{{ t.name }}</span>
                <span v-if="t.kind" class="dc-view">{{ kindAbbrev(t.kind) }}</span>
              </button>
            </template>
          </div>
        </div>
      </aside>

      <main class="dc-main">
        <template v-if="sel">
          <div class="dc-detail-head">
            <span class="dc-detail-title">{{ sel.name }}</span>
            <span class="dc-chip" :class="layerClass(sel.layer)">{{ sel.layer }}</span>
            <span v-if="sel.kind" class="dc-view">{{ pretty(sel.kind) }}</span>
            <span class="dc-count">{{ sel.schema }}</span>
            <button type="button" class="dc-close" @click="selected = null">
              {{ activeWalkObj ? '← Back to walkthrough' : 'Close' }}
            </button>
          </div>
          <p class="dc-connect">
            <span v-if="selUpstream.length">built from {{ selUpstream.length }}</span>
            <span v-if="selDownstream.length">feeds {{ selDownstream.length }}</span>
            <span v-if="selJoins.length">{{ selJoins.length }} join partners</span>
          </p>

          <p v-if="sel.desc" class="dc-desc">{{ sel.desc }}</p>
          <p v-else class="dc-desc dc-muted">No description yet.</p>

          <div v-if="sel.grain" class="dc-grain">
            <span class="dc-fact-label">one row per</span>
            <code v-for="c in sel.grain" :key="c" class="dc-key">{{ c }}</code>
            <span v-if="sel.grain_inexact" class="dc-muted">(approximate — grain includes expressions)</span>
          </div>

          <ul v-if="sel.caveats" class="dc-caveats">
            <li v-for="(c, i) in sel.caveats" :key="i">{{ c }}</li>
          </ul>

          <div v-if="sel.kind" class="dc-kindnote">
            <span class="dc-view">{{ pretty(sel.kind) }}</span>
            {{ kindNote(sel.kind) }}
          </div>

          <div v-if="sel.kind" class="dc-sql">
            <div class="dc-sql-head">
              <span class="dc-fact-label">definition (DDL)</span>
              <span>
                <button v-if="ddlOpen && selDdl" type="button" class="dc-close" @click="copyDdl">
                  {{ ddlCopied ? 'Copied' : 'Copy DDL' }}
                </button>
                <button type="button" class="dc-close" @click="toggleDdl">
                  {{ ddlOpen ? 'Hide' : (sel.kind === 'derived' ? 'SHOW BUILD SQL' : 'SHOW DDL') }}
                </button>
              </span>
            </div>
            <pre v-if="ddlOpen && selDdl"><code>{{ selDdl }}</code></pre>
            <p v-else-if="ddlOpen" class="dc-legend">
              {{ ddlState === 'failed' ? (ddlError || 'Could not fetch the definition from Snowflake.') : 'Fetching live from Snowflake…' }}
            </p>
          </div>

          <div v-for="f in extras(sel, KNOWN.table)" :key="f.key" class="dc-extra">
            <span class="dc-fact-label">{{ f.label }}</span>
            <FieldChips :field="f" :resolve="resolveRef" @pick="selectRef" />
          </div>

          <div class="dc-canvas"><svg ref="svg" /></div>
          <p class="dc-legend">
            solid&nbsp;=&nbsp;lineage · dashed&nbsp;=&nbsp;join, labelled with keys and cardinality · click a table to navigate
          </p>
        </template>

        <template v-else-if="activeWalkObj">
          <div class="dc-detail-head">
            <span class="dc-detail-title">{{ activeWalkObj.title }}</span>
            <span class="dc-count">{{ activeWalkObj.goal }}</span>
            <button type="button" class="dc-close" @click="exitWalk">Exit walkthrough</button>
          </div>

          <div class="dc-tour-nav">
            <button type="button" class="dc-step-btn" :disabled="walkStep === 0" @click="walkStep -= 1">
              ← Back
            </button>
            <button
              v-for="(s, i) in activeWalkObj.steps"
              :key="i"
              type="button"
              class="dc-step-dot"
              :class="{ on: i === walkStep, done: i < walkStep }"
              @click="walkStep = i"
            >
              {{ i + 1 }}
            </button>
            <button
              type="button"
              class="dc-step-btn"
              :disabled="walkStep >= activeWalkObj.steps.length - 1"
              @click="walkStep += 1"
            >
              Next →
            </button>
            <span class="dc-count">step {{ walkStep + 1 }} of {{ activeWalkObj.steps.length }}</span>
          </div>

          <div class="dc-tour-step">
            <p v-if="currentStep.text">{{ currentStep.text }}</p>
            <p v-if="currentStep.join" class="dc-join-hint"><code>{{ currentStep.join }}</code></p>
            <span v-for="t in currentStep.tables || []" :key="t" class="dc-tchip" @click="selectRef(t)">
              {{ shortRef(t) }}
            </span>
            <div v-for="f in extras(currentStep, KNOWN.step)" :key="f.key" class="dc-extra">
              <span class="dc-fact-label">{{ f.label }}</span>
              <FieldChips :field="f" :resolve="resolveRef" @pick="selectRef" />
            </div>
          </div>

          <div v-if="walkFragments.length" class="dc-sql">
            <div class="dc-sql-head">
              <span class="dc-fact-label">the query, built so far</span>
              <button type="button" class="dc-close" @click="copySql">
                {{ copied ? 'Copied' : 'Copy SQL' }}
              </button>
            </div>
            <pre><code
              v-for="f in walkFragments"
              :key="f.idx"
              class="dc-sql-frag"
              :class="{ now: f.idx === walkStep }"
            >{{ f.sql }}
</code></pre>
            <p class="dc-legend">
              Each step adds one CTE; the newest block is highlighted. Click a
              table name above to read its entry, then return here — your place
              is kept.
            </p>
          </div>
        </template>

        <template v-else>
          <section v-if="guide.length" class="dc-guide">
            <h2>{{ lede.title }}</h2>
            <p>{{ lede.body }}</p>
            <div class="dc-pills">
              <button
                v-for="g in restOfGuide"
                :key="g.id"
                type="button"
                class="dc-pill"
                :class="{ on: openGuide === g.id }"
                @click="openGuide = openGuide === g.id ? null : g.id"
              >
                {{ g.title }}
              </button>
            </div>
            <article v-if="openGuideSection" class="dc-guide-body">
              <p>{{ openGuideSection.body }}</p>
              <div v-for="f in extras(openGuideSection, KNOWN.guide)" :key="f.key" class="dc-extra">
                <span class="dc-fact-label">{{ f.label }}</span>
                <FieldChips :field="f" :resolve="resolveRef" @pick="selectRef" />
              </div>
            </article>
          </section>

          <section v-if="walkthroughs.length" class="dc-walks">
            <div class="dc-section-title">Worked paths — build your own metric, step by step</div>
            <div class="dc-walk-cards">
              <button
                v-for="w in walkthroughs"
                :key="w.id"
                type="button"
                class="dc-walk dc-walk-launch"
                @click="startWalk(w.id)"
              >
                <span class="dc-walk-title">{{ w.title }}</span>
                <span class="dc-walk-goal">{{ w.goal }}</span>
                <span class="dc-walk-steps">{{ w.steps.length }} steps →</span>
              </button>
            </div>
          </section>

          <section v-for="sec in extraSections" :key="sec.key" class="dc-walks">
            <div class="dc-section-title">{{ sec.label }}</div>
            <p v-if="sec.kind === 'text'" class="dc-desc">{{ sec.value }}</p>
            <div v-else class="dc-walk-cards">
              <div v-for="(item, i) in sec.items" :key="i" class="dc-walk on dc-generic">
                <div v-if="itemTitle(item)" class="dc-walk-title dc-generic-title">{{ itemTitle(item) }}</div>
                <div v-for="f in extras(item, ['id', 'title', 'name'])" :key="f.key" class="dc-extra">
                  <span class="dc-fact-label">{{ f.label }}</span>
                  <FieldChips :field="f" :resolve="resolveRef" @pick="selectRef" />
                </div>
                <p v-if="typeof item === 'string'" class="dc-desc">{{ item }}</p>
              </div>
            </div>
          </section>
        </template>
      </main>
    </div>
  </div>
</template>

<script>
import { h } from 'vue';
import axios from 'axios';
import { copySensitiveText } from '@/utils/demoEgress';
import * as d3 from 'd3';

// The artifact is regenerated nightly and its shape evolves. Fields named in
// KNOWN get the designed treatment; anything else renders generically through
// FieldChips / extraSections so a new field or section shows up on the page
// the day the generator starts emitting it, instead of waiting for this file.
const KNOWN = {
  root: ['$comment', 'generated_at', 'source', 'guide', 'walkthroughs', 'schemas', 'joins'],
  schema: ['name', 'layer', 'visible', 'description', 'tables', 'table_names'],
  table: ['id', 'schema', 'name', 'visible', 'layer', 'kind', 'desc', 'grain', 'grain_inexact', 'caveats', 'built_from'],
  join: ['a', 'b', 'on', 'card', 'uses', 'other', 'flipped'],
  guide: ['id', 'title', 'body'],
  step: ['text', 'tables', 'join', 'sql'],
};

const LAYER_ORDER = ['bronze', 'silver', 'gold'];
const LAYER_HINTS = {
  bronze: 'as the source sent it',
  silver: 'conformed by Kairon — start here',
  gold: 'shaped for a consumer',
};
const FILL = { bronze: '#fdf3e7', silver: '#f2f3f5', gold: '#fdf6dd' };
const STROKE = { bronze: '#ecd3b0', silver: '#d7dade', gold: '#e8d78f' };

const pretty = (k) => String(k).replace(/_/g, ' ');

// Generic renderer for a field of unknown shape: string arrays become chips
// (clickable when they resolve to a table), scalars become text, objects
// become "key: value" lines.
const FieldChips = {
  name: 'FieldChips',
  props: { field: { type: Object, required: true }, resolve: { type: Function, required: true } },
  emits: ['pick'],
  render() {
    const v = this.field.value;
    const chip = (s) => {
      const fqn = this.resolve(String(s));
      return fqn
        ? h('span', { class: 'dc-tchip', onClick: () => this.$emit('pick', fqn) }, String(s).split('.').pop())
        : h('code', { class: 'dc-key' }, String(s));
    };
    if (Array.isArray(v)) {
      if (v.every((x) => typeof x !== 'object' || x === null)) return h('span', v.map(chip));
      return h('div', v.map((o, i) => h('div', { class: 'dc-generic-kv', key: i },
        Object.entries(o || {}).map(([k, x]) => h('span', { class: 'dc-count' }, `${pretty(k)}: ${String(x)} `)))));
    }
    if (v !== null && typeof v === 'object') {
      return h('div', Object.entries(v).map(([k, x]) => h('div', { class: 'dc-generic-kv' },
        [h('span', { class: 'dc-count' }, `${pretty(k)}: `), chip(x)])));
    }
    if (typeof v === 'string' && v.length > 60) return h('p', { class: 'dc-desc' }, v);
    return chip(v);
  },
};

export default {
  name: 'OverviewEtlDataCatalog',
  components: { FieldChips },
  data() {
    return {
      erd: null,
      ddlByTable: {},
      ddlState: 'idle',
      ddlError: '',
      ddlOpen: false,
      ddlCopied: false,
      open: new Set(),
      selected: null,
      openGuide: null,
      activeWalk: null,
      walkStep: 0,
      copied: false,
      query: '',
      KNOWN,
    };
  },
  computed: {
    guide() { return (this.erd && this.erd.guide) || []; },
    walkthroughs() { return (this.erd && this.erd.walkthroughs) || []; },
    schemas() { return (this.erd && this.erd.schemas) || []; },
    joins() { return (this.erd && this.erd.joins) || []; },
    lede() { return this.guide[0] || { title: '', body: '' }; },
    restOfGuide() { return this.guide.slice(1); },
    openGuideSection() { return this.guide.find((g) => g.id === this.openGuide) || null; },
    activeWalkObj() { return this.walkthroughs.find((w) => w.id === this.activeWalk) || null; },
    currentStep() {
      const w = this.activeWalkObj;
      return (w && w.steps[this.walkStep]) || {};
    },
    // SQL fragments up to and including the current step — the query as it
    // exists at this point in the walkthrough.
    walkFragments() {
      const w = this.activeWalkObj;
      if (!w) return [];
      return w.steps
        .map((s, idx) => ({ idx, sql: s.sql }))
        .filter((f) => f.sql && f.idx <= this.walkStep);
    },
    meta() {
      if (!this.erd) return '';
      const parts = [];
      parts.push(`${this.tableIndex.size} tables`);
      if (this.erd.generated_at) parts.push(`generated ${this.erd.generated_at.slice(0, 10)}`);
      return parts.join(' · ');
    },
    // Top-level keys the layout doesn't consume, rendered as generic sections.
    extraSections() {
      if (!this.erd) return [];
      return Object.entries(this.erd)
        .filter(([k]) => !KNOWN.root.includes(k))
        .map(([k, v]) => {
          if (typeof v === 'string') return { key: k, label: pretty(k), kind: 'text', value: v };
          const items = Array.isArray(v) ? v : [v];
          return { key: k, label: pretty(k), kind: 'items', items };
        })
        .filter((s) => s.kind === 'text' || s.items.length);
    },
    tableIndex() {
      const idx = new Map();
      this.schemas.forEach((sc) => {
        Object.entries(sc.tables || {}).forEach(([name, row]) => {
          idx.set(`${sc.name}.${name}`, {
            id: `${sc.name}.${name}`,
            schema: sc.name,
            name,
            layer: row.layer || sc.layer,
            visible: sc.visible,
            ...row,
          });
        });
      });
      return idx;
    },
    nameIndex() {
      const idx = new Map();
      this.tableIndex.forEach((row, fqn) => {
        idx.set(row.name, idx.has(row.name) ? null : fqn);
      });
      return idx;
    },
    feedsIndex() {
      const idx = new Map();
      this.tableIndex.forEach((row, fqn) => {
        (row.built_from || []).forEach((src) => {
          const s = this.resolveRef(src);
          if (!s) return;
          if (!idx.has(s)) idx.set(s, []);
          idx.get(s).push(fqn);
        });
      });
      return idx;
    },
    joinIndex() {
      const idx = new Map();
      this.joins.forEach((j) => {
        const a = this.resolveRef(j.a);
        const b = this.resolveRef(j.b);
        if (!a || !b) return;
        if (!idx.has(a)) idx.set(a, []);
        if (!idx.has(b)) idx.set(b, []);
        idx.get(a).push({ ...j, other: b, flipped: false });
        idx.get(b).push({ ...j, other: a, flipped: true });
      });
      idx.forEach((list) => list.sort((x, y) => (y.uses || 0) - (x.uses || 0)));
      return idx;
    },
    sel() {
      return this.selected ? this.tableIndex.get(this.selected) : null;
    },
    selDdl() {
      return this.ddlByTable[this.selected] || null;
    },
    selUpstream() {
      return ((this.sel && this.sel.built_from) || [])
        .map((r) => this.resolveRef(r)).filter(Boolean);
    },
    selDownstream() {
      return (this.feedsIndex.get(this.selected) || []).slice().sort();
    },
    selJoins() {
      return this.joinIndex.get(this.selected) || [];
    },
    // Layers come from the data; the three medallion layers keep their order
    // and styling, anything new appends with a neutral style.
    layers() {
      const seen = new Set();
      this.tableIndex.forEach((t) => { if (t.visible) seen.add(t.layer); });
      const ordered = LAYER_ORDER.filter((l) => seen.has(l));
      [...seen].filter((l) => !LAYER_ORDER.includes(l)).sort().forEach((l) => ordered.push(l));
      return ordered;
    },
    matcher() {
      const q = this.query.toLowerCase();
      return (t) => !q
        || t.name.toLowerCase().includes(q)
        || (t.desc || '').toLowerCase().includes(q);
    },
    matchCount() {
      let n = 0;
      this.tableIndex.forEach((t) => { if (t.visible && this.matcher(t)) n += 1; });
      return n;
    },
    columns() {
      return this.layers.map((layer) => {
        const bySchema = new Map();
        let total = 0;
        this.schemas.forEach((sc) => {
          if (!sc.visible) return;
          Object.keys(sc.tables || {}).forEach((name) => {
            const t = this.tableIndex.get(`${sc.name}.${name}`);
            if (t.layer !== layer || !this.matcher(t)) return;
            if (!bySchema.has(sc.name)) {
              bySchema.set(sc.name, { schema: sc.name, description: sc.description, tables: [] });
            }
            bySchema.get(sc.name).tables.push(t);
            total += 1;
          });
        });
        return {
          layer,
          hint: LAYER_HINTS[layer] || '',
          total,
          groups: [...bySchema.values()]
            .map((g) => ({ ...g, tables: g.tables.sort((a, b) => a.name.localeCompare(b.name)) }))
            .sort((a, b) => a.schema.localeCompare(b.schema)),
        };
      });
    },
  },
  watch: {
    walkStep() { this.copied = false; },
    selected() {
      this.ddlOpen = false;
      this.ddlCopied = false;
    },
  },
  async mounted() {
    // Served from the backend's knowledge artifact — single source of truth,
    // republished nightly. No static frontend copy exists.
    const { data } = await axios.get('/api/impact_analytics/warehouse-catalog/');
    this.erd = data;
  },
  methods: {
    pretty,
    extras(obj, known) {
      if (!obj || typeof obj !== 'object') return [];
      return Object.entries(obj)
        .filter(([k, v]) => !known.includes(k) && v !== null && v !== undefined && v !== '')
        .map(([k, v]) => ({ key: k, label: pretty(k), value: v, flat: String(v) }));
    },
    itemTitle(item) {
      if (!item || typeof item !== 'object') return '';
      return item.title || item.name || item.id || '';
    },
    layerClass(layer) {
      return LAYER_ORDER.includes(layer) ? `l-${layer}` : 'l-other';
    },
    kindAbbrev(kind) {
      return kind === 'dynamic_table' ? 'dyn' : pretty(kind);
    },
    resolveRef(r) {
      if (!r || typeof r !== 'string') return null;
      if (r.includes('.')) return this.tableIndex.has(r) ? r : null;
      return this.nameIndex.get(r) || null;
    },
    shortRef(r) {
      const fqn = this.resolveRef(r) || r;
      return String(fqn).split('.').pop();
    },
    joinKeyLabel(pair, flipped) {
      if (typeof pair === 'string') return pair;
      const [l, r] = flipped ? [pair[1], pair[0]] : pair;
      return `${l} = ${r}`;
    },
    cardLabel(j) {
      if (!j.card || j.card === 'unknown') return '';
      if (!j.flipped) return j.card;
      return String(j.card).split(':').reverse().join(':');
    },
    key(layer, schema) { return `${layer}|${schema}`; },
    isOpen(layer, schema) {
      return Boolean(this.query) || this.open.has(this.key(layer, schema));
    },
    toggleSchema(layer, schema) {
      const k = this.key(layer, schema);
      const next = new Set(this.open);
      if (next.has(k)) next.delete(k); else next.add(k);
      this.open = next;
    },
    startWalk(id) {
      this.activeWalk = id;
      this.walkStep = 0;
      this.copied = false;
      this.selected = null;
    },
    exitWalk() {
      this.activeWalk = null;
      this.walkStep = 0;
    },
    copySql() {
      const sql = this.walkFragments.map((f) => f.sql).join('\n');
      // SQL can carry literal practice names, TINs/NPIs or ID lists.
      copySensitiveText(sql).then((copied) => {
        if (!copied) return;
        this.copied = true;
        setTimeout(() => { this.copied = false; }, 1600);
      });
    },
    // Definitions come LIVE from Snowflake on demand — GET_DDL for
    // views/dynamic tables, the pipeline's recorded CTAS for post-processing
    // tables. Nothing is stored in the app; cached per table for the page's
    // lifetime.
    async toggleDdl() {
      this.ddlOpen = !this.ddlOpen;
      if (!this.ddlOpen || this.selDdl) return;
      const table = this.selected;
      this.ddlState = 'loading';
      this.ddlError = '';
      try {
        const { data } = await axios.get('/api/impact_analytics/warehouse-catalog/', { params: { ddl: table } });
        this.ddlByTable = { ...this.ddlByTable, [table]: data.ddl };
        this.ddlState = 'idle';
      } catch (err) {
        this.ddlError = (err.response && err.response.data && err.response.data.detail) || '';
        this.ddlState = 'failed';
      }
    },
    kindNote(kind) {
      if (kind === 'view') {
        return 'This is a view — it stores no rows. The definition below runs against its source tables on every query.';
      }
      if (kind === 'dynamic_table') {
        return 'This is a dynamic table — a materialization Snowflake keeps refreshed automatically from the definition below.';
      }
      if (kind === 'derived') {
        return 'This table is rebuilt by the post-processing pipeline — the build SQL below is the exact statement that produced its current contents.';
      }
      return '';
    },
    copyDdl() {
      if (!this.selDdl) return;
      // Live Snowflake DDL can carry literal practice names, TINs/NPIs or ID lists.
      copySensitiveText(this.selDdl).then((copied) => {
        if (!copied) return;
        this.ddlCopied = true;
        setTimeout(() => { this.ddlCopied = false; }, 1600);
      });
    },
    selectRef(r) {
      const fqn = this.resolveRef(r);
      if (fqn) this.selectTable(fqn);
    },
    selectTable(id) {
      this.selected = this.selected === id ? null : id;
      if (this.selected) this.$nextTick(() => this.draw());
    },
    // Deterministic four-column hub: bronze | silver | SELECTED | gold.
    // Lineage edges are solid; join edges are dashed and carry the join key +
    // cardinality as an edge label, so the picture holds everything the old
    // built-from/feeds/joins lists said.
    draw() {
      const el = this.$refs.svg;
      if (!el || !this.sel) return;
      const svg = d3.select(el);
      svg.selectAll('*').remove();

      const centre = { id: this.selected, layer: this.sel.layer, centre: true };
      const neighbours = new Map();
      const add = (fqn, lineage) => {
        if (!fqn || fqn === this.selected) return;
        const t = this.tableIndex.get(fqn);
        if (!t) return;
        if (!neighbours.has(fqn)) neighbours.set(fqn, { id: fqn, layer: t.layer, lineage: false, join: null });
        if (lineage) neighbours.get(fqn).lineage = true;
      };
      this.selUpstream.forEach((f) => add(f, true));
      this.selDownstream.forEach((f) => add(f, true));
      this.selJoins.forEach((j) => {
        add(j.other, false);
        if (neighbours.has(j.other)) neighbours.get(j.other).join = j;
      });

      const NODE_W = 210;
      const NODE_H = 24;
      const ROW = 38;
      const GAP = 170;
      const pad = 16;

      // Only columns that actually hold nodes are laid out — a table whose
      // neighbours are all silver draws two columns, not a fixed four with
      // empty gutters pushing the canvas off the pane.
      const buckets = { bronze: [], silver: [], gold: [] };
      neighbours.forEach((n) => (buckets[n.layer] || buckets.silver).push(n));
      const cols = [
        buckets.bronze, buckets.silver, [centre], buckets.gold,
      ].filter((arr) => arr.length);
      cols.forEach((arr) => arr.sort((a, b) => a.id.localeCompare(b.id)));

      const tallest = Math.max(...cols.map((a) => a.length), 1);
      const pos = new Map();
      cols.forEach((arr, ci) => {
        const offset = (tallest - arr.length) / 2;
        arr.forEach((n, i) => pos.set(n.id, {
          x: pad + ci * (NODE_W + GAP),
          y: pad + (offset + i) * ROW + NODE_H / 2,
        }));
      });

      const width = pad * 2 + (cols.length - 1) * (NODE_W + GAP) + NODE_W;
      const height = pad * 2 + tallest * ROW;
      // viewBox + max-width lets small diagrams render at natural size and
      // large ones shrink to the pane instead of overflowing it.
      svg.attr('viewBox', `0 0 ${width} ${height}`)
        .attr('preserveAspectRatio', 'xMinYMin meet')
        .style('width', '100%')
        .style('max-width', `${width}px`)
        .style('height', 'auto');

      const link = d3.linkHorizontal().x((d) => d.x).y((d) => d.y);
      const c = pos.get(centre.id);
      const edges = [...neighbours.values()].map((n) => {
        const p = pos.get(n.id);
        const leftward = p.x < c.x;
        const x1 = leftward ? c.x : c.x + NODE_W;
        const x2 = leftward ? p.x + NODE_W : p.x;
        let label = '';
        if (n.join) {
          const keys = (n.join.on || []).slice(0, 2)
            .map((pair) => this.joinKeyLabel(pair, n.join.flipped)).join(', ');
          const card = this.cardLabel(n.join);
          label = [keys, card].filter(Boolean).join('  ');
          if (label.length > 40) label = `${label.slice(0, 39)}…`;
        }
        // Each label sits in the gap beside its own neighbour's row, so two
        // labels can never share a y and collide near the centre.
        return {
          d: link({ source: { x: x1, y: c.y }, target: { x: x2, y: p.y } }),
          dashed: !n.lineage,
          label,
          lx: leftward ? x2 + 8 : x2 - 8,
          anchor: leftward ? 'start' : 'end',
          ly: p.y - 9,
        };
      });
      svg.append('g').selectAll('path').data(edges).join('path')
        .attr('d', (d) => d.d)
        .attr('fill', 'none')
        .attr('stroke', '#ddddd5')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', (d) => (d.dashed ? '4 3' : null));
      svg.append('g').selectAll('text').data(edges.filter((d) => d.label)).join('text')
        .attr('x', (d) => d.lx)
        .attr('y', (d) => d.ly)
        .attr('text-anchor', (d) => d.anchor)
        .attr('font-size', 10)
        .attr('fill', '#8a8a80')
        .attr('paint-order', 'stroke')
        .attr('stroke', '#fafaf8')
        .attr('stroke-width', 3)
        .text((d) => d.label);

      const all = [centre, ...neighbours.values()];
      const g = svg.append('g').selectAll('g').data(all).join('g')
        .attr('transform', (n) => `translate(${pos.get(n.id).x},${pos.get(n.id).y})`)
        .style('cursor', 'pointer')
        .on('click', (ev, n) => { if (!n.centre) this.selectTable(n.id); });
      g.append('rect')
        .attr('y', -NODE_H / 2).attr('width', NODE_W).attr('height', NODE_H).attr('rx', 4)
        .attr('fill', (n) => FILL[n.layer] || FILL.silver)
        .attr('stroke', (n) => (n.centre ? '#8a5a20' : STROKE[n.layer] || STROKE.silver))
        .attr('stroke-width', (n) => (n.centre ? 2 : 1))
        .attr('stroke-dasharray', (n) => {
          const t = this.tableIndex.get(n.id);
          return t && t.kind ? '3 2' : null;
        });
      g.append('text')
        .attr('x', 8).attr('y', 4)
        .attr('font-size', 12)
        .attr('font-weight', (n) => (n.centre ? 600 : 400))
        .attr('fill', '#344054')
        .text((n) => {
          const t = n.id.split('.').pop();
          return t.length > 27 ? `${t.slice(0, 26)}…` : t;
        });
    },
  },
};
</script>

<style scoped>
.dc {
  --fs-body: 13px;
  --fs-meta: 12px;
  background: #fff;
  border: 1px solid #e3e3de;
  border-radius: 10px;
  color: #23231f;
  font-size: var(--fs-body);
  overflow: hidden;
}

.dc-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 18px;
  border-bottom: 1px solid #ebebe6;
}
.dc-search {
  font-family: inherit;
  font-size: var(--fs-body);
  border: 1px solid #e3e3de;
  border-radius: 6px;
  padding: 5px 10px;
  width: 300px;
  max-width: 100%;
  color: #23231f;
  background: #fff;
}
.dc-search:focus { outline: none; border-color: oklch(0.8 0.08 55); }
.dc-meta { margin-left: auto; }

.dc-body { display: flex; align-items: stretch; min-height: 480px; }

.dc-list {
  width: 320px;
  flex-shrink: 0;
  border-right: 1px solid #f1f1ec;
  overflow-y: auto;
  max-height: 78vh;
  padding: 12px 0 20px;
}
.dc-layer { margin-bottom: 10px; }
.dc-layer-head { display: flex; align-items: center; gap: 8px; padding: 6px 18px 8px; }
.dc-layer-hint { font-size: 11px; color: #b0b0a6; }

.dc-main { flex: 1; min-width: 0; padding: 14px 18px 20px; overflow-x: hidden; }

.dc-detail-head { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.dc-detail-title { font-size: 14px; font-weight: 600; }
.dc-close {
  margin-left: auto;
  font-family: inherit;
  font-size: var(--fs-meta);
  background: #fff;
  border: 1px solid #e3e3de;
  border-radius: 5px;
  padding: 3px 9px;
  cursor: pointer;
  color: #7d7d73;
}
.dc-close:hover { border-color: #cfcfc7; color: #23231f; }
.dc-connect { margin: 4px 0 0; display: flex; gap: 14px; font-size: var(--fs-meta); color: #a5a59a; }
.dc-desc { margin: 8px 0 0; max-width: 80ch; line-height: 1.55; color: #4b4b44; }
.dc-muted { color: #a5a59a; }
.dc-grain { margin-top: 8px; display: flex; align-items: center; flex-wrap: wrap; gap: 5px; }
.dc-caveats { margin: 8px 0 0; padding-left: 18px; }
.dc-caveats li { color: #8a5a20; line-height: 1.5; max-width: 78ch; }
.dc-canvas { overflow-x: auto; padding: 12px 0 2px; background: #fafaf8; border: 1px solid #f1f1ec; border-radius: 8px; margin-top: 12px; }
.dc-canvas svg { display: block; }
.dc-legend { margin: 6px 0 0; font-size: 11px; color: #b0b0a6; }
.dc-extra { margin: 6px 0 0; display: flex; align-items: baseline; flex-wrap: wrap; gap: 6px; }
.dc-fact-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #a5a59a;
}
.dc-key {
  font-size: 11px;
  background: #fff;
  border: 1px solid #e8e8e2;
  border-radius: 4px;
  padding: 1px 5px;
  color: #5b616b;
}
.dc-tchip {
  display: inline-block;
  font-size: var(--fs-meta);
  background: #fff;
  border: 1px solid #e3e3de;
  border-radius: 5px;
  padding: 1px 7px;
  margin: 2px 4px 2px 0;
  cursor: pointer;
  color: #344054;
}
.dc-tchip:hover { border-color: oklch(0.8 0.08 55); color: oklch(0.45 0.13 45); }
.dc-generic { padding: 8px 12px; }
.dc-generic-title { padding-bottom: 4px; }
.dc-generic-kv { margin: 2px 0; }

.dc-guide h2 { font-size: 14px; font-weight: 600; margin: 0 0 6px; }
.dc-guide > p { margin: 0 0 12px; max-width: 76ch; line-height: 1.55; color: #4b4b44; }
.dc-pills { display: flex; flex-wrap: wrap; gap: 6px; }
.dc-pill {
  font-family: inherit;
  font-size: var(--fs-meta);
  background: #fff;
  border: 1px solid #e3e3de;
  border-radius: 20px;
  padding: 3px 11px;
  cursor: pointer;
  color: #7d7d73;
}
.dc-pill:hover { border-color: #cfcfc7; color: #23231f; }
.dc-pill.on { background: oklch(0.96 0.03 55); border-color: oklch(0.8 0.08 55); color: oklch(0.45 0.13 45); }
.dc-guide-body p { margin: 12px 0 0; max-width: 76ch; line-height: 1.55; color: #4b4b44; }

.dc-walks { margin-top: 18px; }
.dc-section-title { font-size: var(--fs-body); font-weight: 600; padding-bottom: 10px; }
.dc-walk-cards { display: flex; flex-direction: column; gap: 6px; }
.dc-walk { border: 1px solid #ebebe6; border-radius: 8px; }
.dc-walk.on { border-color: #ddddd5; background: #fafaf8; }
.dc-walk-head {
  display: flex;
  align-items: baseline;
  gap: 10px;
  width: 100%;
  text-align: left;
  font-family: inherit;
  font-size: var(--fs-body);
  background: none;
  border: 0;
  padding: 8px 12px;
  cursor: pointer;
  color: #344054;
}
.dc-walk-title { font-weight: 600; white-space: nowrap; }
.dc-walk-goal { font-size: var(--fs-meta); color: #a5a59a; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.dc-join-hint code { font-size: 11px; color: #7a6212; }
.dc-walk-launch {
  display: flex;
  align-items: baseline;
  gap: 10px;
  width: 100%;
  text-align: left;
  font-family: inherit;
  font-size: var(--fs-body);
  background: none;
  padding: 10px 12px;
  cursor: pointer;
  color: #344054;
}
.dc-walk-launch:hover { border-color: oklch(0.8 0.08 55); background: #fafaf8; }
.dc-walk-steps { margin-left: auto; font-size: var(--fs-meta); color: oklch(0.55 0.16 45); white-space: nowrap; }

.dc-tour-nav { display: flex; align-items: center; gap: 6px; margin-top: 12px; flex-wrap: wrap; }
.dc-step-btn {
  font-family: inherit;
  font-size: var(--fs-meta);
  background: #fff;
  border: 1px solid #e3e3de;
  border-radius: 6px;
  padding: 4px 10px;
  cursor: pointer;
  color: #344054;
}
.dc-step-btn:hover:not(:disabled) { border-color: oklch(0.8 0.08 55); color: oklch(0.45 0.13 45); }
.dc-step-btn:disabled { color: #c9c9c0; cursor: default; }
.dc-step-dot {
  font-family: inherit;
  font-size: var(--fs-meta);
  font-variant-numeric: tabular-nums;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 1px solid #e3e3de;
  background: #fff;
  cursor: pointer;
  color: #7d7d73;
}
.dc-step-dot.done { border-color: #ecd3b0; color: #8a5a20; }
.dc-step-dot.on { background: oklch(0.96 0.03 55); border-color: oklch(0.8 0.08 55); color: oklch(0.45 0.13 45); font-weight: 600; }
.dc-tour-step { margin-top: 12px; }
.dc-tour-step > p { margin: 0 0 8px; max-width: 78ch; line-height: 1.6; color: #4b4b44; }

.dc-sql { margin-top: 14px; }
.dc-sql-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; }
.dc-sql pre {
  margin: 0;
  background: #fafaf8;
  border: 1px solid #f1f1ec;
  border-radius: 8px;
  padding: 12px 14px;
  overflow-x: auto;
  font-size: 12px;
  line-height: 1.55;
}
.dc-sql-frag { display: block; color: #a5a59a; transition: color 0.2s; }
.dc-sql-frag.now { color: #23231f; }
.dc-sql pre code { color: #23231f; }

.dc-kindnote {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 8px 12px;
  background: #fafaf8;
  border: 1px solid #f1f1ec;
  border-radius: 8px;
  color: #4b4b44;
  font-size: 13px;
}

.dc-chip {
  font-size: var(--fs-meta);
  font-weight: 600;
  border-radius: 20px;
  padding: 2px 9px;
  border: 1px solid;
}
.dc-chip.l-bronze { background: #fdf3e7; border-color: #ecd3b0; color: #8a5a20; }
.dc-chip.l-silver { background: #f2f3f5; border-color: #d7dade; color: #5b616b; }
.dc-chip.l-gold { background: #fdf6dd; border-color: #e8d78f; color: #7a6212; }
.dc-chip.l-other { background: #f2f3f5; border-color: #d7dade; color: #5b616b; }
.dc-count { font-size: var(--fs-meta); color: #a5a59a; font-variant-numeric: tabular-nums; }

.dc-group { margin-bottom: 2px; }
.dc-schema {
  display: flex;
  align-items: center;
  gap: 7px;
  width: 100%;
  text-align: left;
  font-family: inherit;
  font-size: var(--fs-meta);
  color: #7d7d73;
  background: none;
  border: 0;
  padding: 4px 18px;
  cursor: pointer;
}
.dc-schema:hover { background: #fafaf8; color: #23231f; }
.dc-caret { color: #b0b0a6; width: 8px; flex-shrink: 0; }
.dc-trunc { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1; }
.dc-row {
  display: flex;
  align-items: center;
  gap: 7px;
  width: 100%;
  text-align: left;
  font-family: inherit;
  font-size: var(--fs-body);
  color: #344054;
  background: none;
  border: 0;
  padding: 3px 18px 3px 33px;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.dc-row:hover { background: #fafaf8; color: oklch(0.55 0.16 45); }
.dc-row.on { background: oklch(0.96 0.03 55); color: oklch(0.45 0.13 45); font-weight: 500; }
.dc-view {
  font-size: var(--fs-meta);
  color: #9a9a90;
  border: 1px dashed #d3d3cb;
  border-radius: 4px;
  padding: 0 4px;
  flex-shrink: 0;
}
</style>
